/* The fixed wording of a product's PDP record (designs/products-pdp.md PP18)
   and the small derivations the table and the record heading share. Exact
   strings are what the tests and the review hold the page to. */
import type { CurrentDeclaration, ProductPdp } from "../../../../shared/product-pdp";
import { emptyProductPdp } from "../../../../shared/product-pdp";
/* the PDP version a product follows is the catalog's current version (UC constraint) */
import { PDP_VERSION, type PdpGate } from "../../../../shared/pdp";
import type { Role } from "../../../../shared/roles";

/* a calendar day ("2026-06-03") renders in UTC so a container's clock cannot
   move it — the PdpView idiom */
const utc = (day: string): Date => new Date(`${day}T00:00:00.000Z`);

export const fmtDay = (day: string): string =>
  utc(day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export const fmtMonthYear = (day: string): string =>
  utc(day).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

/* an ISO instant renders as its UTC calendar day */
export const fmtStampDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/* the record's PDP record, or the honest empty one */
export const pdpFor = (records: ProductPdp[], productId: string): ProductPdp =>
  records.find((p) => p.productId === productId) ?? emptyProductPdp(productId);

/* PP18: `Phase {n} declared{ by name} · effective {Mon d, yyyy} · PDP {version}`
   and `Not in the PDP — {reason}{ by name} · {date}` */
export function declarationStamp(decl: CurrentDeclaration): string {
  const by = decl.recorded.by ? ` by ${decl.recorded.by.name}` : "";
  if (decl.kind === "phase")
    return `Phase ${decl.phase} declared${by} · effective ${fmtDay(decl.effective)} · PDP ${PDP_VERSION}`;
  return `Not in the PDP — ${decl.reason}${by} · ${fmtStampDate(decl.recorded.date)}`;
}

export const roleName = (roles: Role[], id: string): string => roles.find((r) => r.id === id)?.name ?? id;

/* the gate that follows the declared phase — none after Phase 6 */
export const gateAfter = (gates: PdpGate[], phase: number): PdpGate | undefined =>
  gates.find((g) => g.afterPhase === phase);
